import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * Clears loading spinner state on add-to-cart buttons (used before showing success UI).
 * @param {NodeListOf<AddToCartComponent> | AddToCartComponent[]} containers
 */
function clearAddToCartButtonLoading(containers) {
  for (const container of containers) {
    const btn = container.refs.addToCartButton;
    if (btn) {
      delete btn.dataset.loading;
      btn.removeAttribute('aria-busy');
    }
  }
}

/**
 * Shows ATC success animation (matches `data-added` styles in add-to-cart-button.liquid).
 * @param {NodeListOf<AddToCartComponent> | AddToCartComponent[]} containers
 */
function applyAddToCartSuccessVisual(containers) {
  clearAddToCartButtonLoading(containers);
  for (const container of containers) {
    const btn = container.refs.addToCartButton;
    if (btn) {
      btn.dataset.added = 'true';
    }
  }
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        return;
      }
    }
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 * @property {HTMLInputElement | undefined} translatedSizeProperty - Hidden `properties[_translated_size]` input, present only when the product has a Size option.
 * @property {HTMLInputElement | undefined} compareAtPriceProperty - Hidden `properties[_compare_at_price]` input, present only when the current variant is on sale.
 * @property {HTMLInputElement | undefined} barcodeProperty - Hidden `properties[_barcode]` input, present only when the current variant has no SKU.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** Variant ID from the most recent OOS click — used in the notify-me:open payload. */
  #lastOosVariantId = null;

  connectedCallback() {
    super.connectedCallback();

    // Recreate on every connect — disconnectedCallback aborts the previous controller,
    // so reusing it would silently drop all addEventListener calls.
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    target?.addEventListener('variant:oos-clicked', this.#onOosClicked, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Initial-load notify trigger: if the page loaded with an OOS-eligible variant
    // already selected, set notifyMode so the button shows the correct label from
    // the first render (matching image-9 / AC1).
    requestAnimationFrame(() => {
      this.#maybeEnterNotifyModeOnLoad();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Notify mode: OOS variant selected — open the Notify Me modal instead of submitting.
    const addToCartButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (addToCartButton?.dataset.notifyMode === 'true') {
      if (this.#isInQuickView()) return;
      const detail = { scenario: this.dataset.notifyScenario || 'soldout' };
      if (this.#lastOosVariantId) detail.variantId = this.#lastOosVariantId;

      // Enrich the payload with the current product's data from the adjacent island
      // so the modal opens with the correct variants after a sibling colour switch.
      const configEl = this.querySelector('[data-notify-config]');
      const templateEl = this.querySelector('[data-notify-product-details]');
      if (configEl?.textContent) {
        try {
          const productConfig = JSON.parse(configEl.textContent);
          Object.assign(detail, productConfig);
        } catch {
          // Malformed island — proceed without enrichment; modal opens in degraded state.
        }
      }
      if (templateEl) {
        detail.productDetailsHtml = /** @type {HTMLTemplateElement} */ (templateEl).innerHTML;
      }

      document.dispatchEvent(new CustomEvent('notify-me:open', { bubbles: true, detail }));
      return;
    }

    // Validate that all variant options have been selected (PDP: .shopify-section; quick view: dialog)
    const variantPicker = this.closest('.shopify-section, dialog')?.querySelector('variant-picker');
    if (variantPicker) {
      const unselectedFieldsets = variantPicker.getAllUnselectedFieldsets?.()
        || [variantPicker.getFirstUnselectedFieldset?.()].filter(Boolean);
      if (unselectedFieldsets.length > 0) {
        for (const fieldset of unselectedFieldsets) {
          const validation = fieldset.querySelector(':scope > .variant-option__validation');
          if (validation) {
            validation.removeAttribute('hidden');
          }
          const details = fieldset.querySelector('details.variant-option__dropdown-details');
          if (details) {
            details.classList.add('variant-option__dropdown-details--error');
          }
        }
        const targetFieldset = unselectedFieldsets[0];
        // Focus the trigger control, not the fieldset (which can grow taller than the
        // viewport once its validation message shows). Pills fall back to the fieldset.
        const focusTarget = targetFieldset.querySelector('summary.variant-option__dropdown-trigger') || targetFieldset;

        // The sticky header can overlay the top of the viewport — account for its height
        // using the same offset pattern already used elsewhere in the theme.
        const headerEl = document.querySelector('#header-component, header-component, .header');
        const headerHeight = headerEl
          ? parseFloat(getComputedStyle(headerEl).getPropertyValue('--header-height')) ||
            headerEl.getBoundingClientRect().height
          : 0;

        // Scroll only if the label (fieldset top) isn't in view yet — checking just the
        // top edge keeps this stable even though the fieldset can exceed viewport height.
        const targetRect = targetFieldset.getBoundingClientRect();
        const isTargetInView = targetRect.top >= headerHeight && targetRect.top < window.innerHeight;
        if (!isTargetInView) {
          // Land the label just below the sticky header rather than at the very top of the
          // viewport, where the header would otherwise cover it.
          const scrollToY = Math.max(0, targetRect.top + window.scrollY - headerHeight - 8);
          window.scrollTo({ top: scrollToY, behavior: 'smooth' });
        }
        focusTarget.focus({ preventScroll: true });
        return;
      }
    }

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    for (const container of allAddToCartContainers) {
      const btn = container.refs.addToCartButton;
      if (btn) {
        btn.removeAttribute('data-added');
      }
    }

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    // Set loading state on all add-to-cart buttons
    for (const container of allAddToCartContainers) {
      const btn = container.refs.addToCartButton;
      if (btn) {
        btn.dataset.loading = 'true';
        btn.setAttribute('aria-busy', 'true');
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });
    const skipAutoOpenCartDrawer = false;

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              skipAutoOpenCartDrawer,
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          const requestedQty = Number(formData.get('quantity')) || 1;
          const variantIdStr = String(id).trim();
          const qtyInput = this.refs.quantitySelector?.quantityInput;
          const previousInCart = qtyInput ? Number(qtyInput.getAttribute('data-cart-quantity')) || 0 : 0;
          let actualTotalForVariant = 0;
          let foundVariantInResponse = false;
          for (const item of response.items ?? []) {
            if (item == null || item.variant_id == null) continue;
            if (String(item.variant_id) !== variantIdStr) continue;
            foundVariantInResponse = true;
            actualTotalForVariant += Number(item.quantity) || 0;
          }
          const expectedTotal = previousInCart + requestedQty;
          const quantityWasCapped =
            foundVariantInResponse && expectedTotal > 0 && actualTotalForVariant < expectedTotal;
          if (quantityWasCapped && addToCartTextError) {
            const capMsg = String(Theme.translations.cart_quantity_capped_message || '').replace(
              /\{\{\s*count\s*\}\}/gi,
              String(actualTotalForVariant)
            );
            addToCartTextError.classList.remove('hidden');
            const capTextNode = addToCartTextError.childNodes[2];
            if (capTextNode) {
              capTextNode.textContent = capMsg;
            } else {
              addToCartTextError.appendChild(document.createTextNode(capMsg));
            }
            this.#setLiveRegionText(capMsg);
            if (this.#timeout) clearTimeout(this.#timeout);
            this.#timeout = setTimeout(() => {
              if (!addToCartTextError) return;
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton && !quantityWasCapped) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();

          applyAddToCartSuccessVisual(allAddToCartContainers);

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              skipAutoOpenCartDrawer,
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        for (const container of allAddToCartContainers) {
          const btn = container.refs.addToCartButton;
          if (btn) {
            delete btn.dataset.loading;
            btn.removeAttribute('aria-busy');
          }
        }
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, translatedSizeProperty, compareAtPriceProperty, barcodeProperty } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    // Refresh the translated Size line item property to match the newly selected
    // variant's locale-translated option value (Translate & Adapt applies at the
    // Liquid drop level, so the freshly fetched HTML already reflects it).
    if (translatedSizeProperty) {
      const newTranslatedSizeProperty = event.detail.data.html.querySelector(
        'input[name="properties[_translated_size]"]'
      );
      translatedSizeProperty.value =
        newTranslatedSizeProperty instanceof HTMLInputElement ? newTranslatedSizeProperty.value : '';
    }

    // Refresh compare-at-price for the newly selected variant — sale state differs per
    // size/colour, and unlike translatedSizeProperty this input can appear/disappear.
    const newCompareAtPriceProperty = event.detail.data.html.querySelector(
      'input[name="properties[_compare_at_price]"]'
    );
    this.#morphOrUpdateElement(compareAtPriceProperty, newCompareAtPriceProperty, this.refs.productFormButtons);

    // Refresh the barcode fallback property for the newly selected variant — present only
    // when that variant has no SKU, so (like compareAtPriceProperty) it can appear/disappear.
    const newBarcodeProperty = event.detail.data.html.querySelector(
      'input[name="properties[_barcode]"]'
    );
    this.#morphOrUpdateElement(barcodeProperty, newBarcodeProperty, this.refs.productFormButtons);

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Clear Notify mode and retained variant on in-stock variant update
    if (currentAddToCartButton) {
      delete currentAddToCartButton.dataset.notifyMode;
      currentAddToCartButton.removeAttribute('data-added');
      this.#lastOosVariantId = null;
      const icon = currentAddToCartButton.querySelector('.add-to-cart-icon');
      if (icon) {
        /** @type {HTMLElement} */ (icon).style.display = '';
      }
    }

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.dataset.notifyMode === 'true') return;
    this.refs.addToCartButtonContainer?.disable();
  };

  /**
   * Handles OOS variant click — re-enables button and enters Notify mode.
   * Gated by the A6 eligibility flag (data-notify-eligible) from buy-buttons.liquid.
   * This counteracts #onVariantSelected's disable since no VariantUpdateEvent follows for OOS.
   * @param {CustomEvent} event
   */
  #onOosClicked = (event) => {
    if (this.#isInQuickView()) return;
    if (!this.#isNotifyEligible()) return;

    // Retain the variant ID so the notify-me:open payload can carry it.
    if (event.detail?.variantId) {
      this.#lastOosVariantId = Number(event.detail.variantId);
    }

    const { addToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    if (!addToCartButtonContainer) return;

    addToCartButtonContainer.enable();
    const button = addToCartButtonContainer.refs.addToCartButton;
    if (button) {
      button.removeAttribute('data-added');
      button.dataset.notifyMode = 'true';
      const textContent = button.querySelector('.add-to-cart-text__content span');
      // Guard: skip label update when data-notify-text is absent to avoid empty text.
      if (textContent && this.dataset.notifyText) {
        textContent.textContent = this.dataset.notifyText;
      }
      const icon = button.querySelector('.add-to-cart-icon');
      if (icon) {
        /** @type {HTMLElement} */ (icon).style.display = 'none';
      }
    }

    if (acceleratedCheckoutButtonContainer) {
      acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
    }
  };

  /**
   * Returns true when the A6 eligibility flag (data-notify-eligible) allows the
   * Notify Me workflow. When false (all-OOS, no override), the button stays "Sold out".
   * @returns {boolean}
   */
  #isNotifyEligible() {
    return this.dataset.notifyEligible !== 'false';
  }

  /**
   * Returns true when this form is inside a quick-add-dialog. Used to suppress
   * Notify Me entry points in the quick-view.
   * @returns {boolean}
   */
  #isInQuickView() {
    return !!this.closest('quick-add-dialog');
  }

  /**
   * Initial-load trigger: buy-buttons.liquid emits data-initial-notify-mode="true"
   * when the server-rendered initially-selected variant is OOS and notify-eligible.
   * This enters notifyMode on page load so the button shows "Notify me when back in stock"
   * without the shopper needing to re-click the already-selected OOS size.
   */
  #maybeEnterNotifyModeOnLoad() {
    if (this.#isInQuickView()) return;
    if (this.dataset.initialNotifyMode !== 'true') return;

    const { addToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    if (!addToCartButtonContainer) return;

    addToCartButtonContainer.enable();
    const button = addToCartButtonContainer.refs.addToCartButton;
    if (button) {
      button.dataset.notifyMode = 'true';
      const textContent = button.querySelector('.add-to-cart-text__content span');
      // Guard: skip label update when data-notify-text is absent to avoid empty text.
      if (textContent && this.dataset.notifyText) {
        textContent.textContent = this.dataset.notifyText;
      }
      const icon = button.querySelector('.add-to-cart-icon');
      if (icon) {
        /** @type {HTMLElement} */ (icon).style.display = 'none';
      }
    }
    if (acceleratedCheckoutButtonContainer) {
      acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
    }
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
